import { CircularProgress, MenuItem } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Form } from "formik";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { getTrasladosReferentes } from "../../../actions/traslados";
import FormikTextField from "../../commons/formik/formikTextField";
import { StyledDialog } from "../../styled/dialog";
import FormikActions from "../formik/formikActions";
import FormikAutocomplete from "../formik/formikAutocomplete";
import FormikForm from "../formik/formikForm";
import FormikSelect from "../formik/formikSelect";
import FormikSelectMultiple from "../formik/formikSelectMultiple";
import CustomDialog from "./customDialog";

const TRASLADOS_TIPOS = ["Agente de Traslados", "Gestor de Siniestros", "Prestador de traslado"];
const GESTOR_SINIESTROS_TIPO = "Gestor de Siniestros";

const isTrasladosTipo = (tipo) => TRASLADOS_TIPOS.includes(tipo);

const validation = (roles) =>
  Yup.object({
    nombre: Yup.string().required("Campo obligatorio"),
    apellido: Yup.string().required("Campo obligatorio"),
    email: Yup.string().required("Campo obligatorio").email("Email inválido"),
    idRol: Yup.string().required("Campo obligatorio"),
    idArt: Yup.string()
      .nullable()
      .test({
        name: "art",
        message: "Campo obligatorio",
        test: function (value) {
          let rol = roles.find((r) => r.id == this.parent.idRol);
          if (rol && (rol.tipo.includes("Art") || isTrasladosTipo(rol.tipo))) {
            return value;
          } else return true;
        },
      }),
    prestadores: Yup.array()
      .nullable()
      .test({
        name: "prestadores",
        message: "Campo obligatorio",
        test: function (value) {
          let rol = roles.find((r) => r.id == this.parent.idRol);
          if (rol?.tipo === "Prestador administrativo" || rol?.tipo === "Prestador de traslado") {
            return value && value.length > 0;
          } else return true;
        },
      }),
    referenteGestor: Yup.mixed()
      .nullable()
      .test({
        name: "referenteGestor",
        message: "Campo obligatorio",
        test: function (value) {
          const rol = roles.find((r) => r.id == this.parent.idRol);
          if (rol?.tipo === GESTOR_SINIESTROS_TIPO) {
            return value != null && value !== "";
          }
          return true;
        },
      }),
  });

const LoadReferentesOnArt = ({ open, idRol, idArt, isGestorSiniestros }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (open && isGestorSiniestros && idArt) {
      dispatch(getTrasladosReferentes(idArt));
    }
  }, [open, isGestorSiniestros, idArt, idRol, dispatch]);

  return null;
};

const EditUserDialog = ({
  user,
  open,
  handleClose,
  addingNewUser,
  handleSubmit,
  availableRoles,
  loading,
}) => {
  const dispatch = useDispatch();
  const userRole = useSelector((state) => state.auth.roles);
  const roles = useSelector((state) => state.users.roles);
  const arts = useSelector((state) => state.arts.list);
  const providers = useSelector((state) => state.providers.listProviders.list);
  const referentes = useSelector((state) => state.traslados.referentes);

  const rolesList = useMemo(() => {
    if (availableRoles) {
      return roles.data.filter((r) => availableRoles.includes(r.tipo));
    }
    return roles.data;
  }, [availableRoles, roles.data]);

  const isArtRol = (idRol) => {
    let rol = roles.data.find((r) => r.id === idRol);
    if (rol && rol.tipo.includes("Art")) {
      return true;
    } else return false;
  };

  const isPrestadorRol = (idRol) => {
    let rol = roles.data.find((r) => r.id === idRol);
    if (rol?.tipo === "Prestador administrativo" || rol?.tipo === "Prestador de traslado") {
      return true;
    } else return false;
  };

  const isTrasladosRol = (idRol) => {
    const rol = roles.data.find((r) => r.id === idRol);
    return Boolean(rol && isTrasladosTipo(rol.tipo));
  };

  const isGestorSiniestrosRol = (idRol) => {
    const rol = roles.data.find((r) => r.id === idRol);
    return rol?.tipo === GESTOR_SINIESTROS_TIPO;
  };

  const handleRolChange = (idRol, setFieldValue) => {
    if (!idRol) return;

    const rol = roles.data.find((r) => r.id === idRol);
    if (!rol) return;

    if (rol.tipo.includes("Soporte")) {
      setFieldValue("prestadores", []);
      setFieldValue("idArt", "");
      setFieldValue("referenteGestor", null);
    } else if (rol.tipo === "Prestador administrativo") {
      setFieldValue("idArt", "");
      setFieldValue("referenteGestor", null);
    } else if (rol.tipo.includes("Art") || isTrasladosTipo(rol.tipo)) {
      setFieldValue("prestadores", []);
      if (rol.tipo !== GESTOR_SINIESTROS_TIPO) {
        setFieldValue("referenteGestor", null);
      }
    }
  };

  const handleArtChange = (art, setFieldValue) => {
    setFieldValue("referenteGestor", null);
    if (art?.id) {
      dispatch(getTrasladosReferentes(art.id));
    }
  };

  return (
    <CustomDialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
    >
      {roles.isFetching || loading ? (
        <CircularProgress
          sx={{
            marginLeft: "290px",
            marginTop: "100px",
            marginBottom: "100px",
          }}
          size={40}
        />
      ) : (
        <>
          <DialogTitle>
            {addingNewUser ? "Agregar Usuario" : "Editar Usuario"}
          </DialogTitle>
          <FormikForm
            enableReinitialize
            initialValues={user}
            validationSchema={() => validation(roles.data)}
            onSubmit={(values) => {
              const payload = { ...values };

              
              const isPT = roles.data.find(r => r.id === values.idRol)?.tipo === "Prestador de traslado";
              if (isPT) {
                  payload.prestadores = (values.prestadores || []).map(id => {
                    const provider = providers.find(p => p.id === id);
                    return {
                      id,
                      nombre: provider?.nombre || ""
                    };
                  });
              } else if (isArtRol(values.idRol) || isTrasladosRol(values.idRol)) {
                payload.prestadores = [];
                payload.idPrestador = null;
              } else if (isPrestadorRol(values.idRol)) {
                payload.idArt = null;
                payload.prestadores = (values.prestadores || []).map(id => {
                  const provider = providers.find(p => p.id === id);
                  return {
                    id,
                    nombre: provider?.nombre || ""
                  };
                });
                payload.idPrestador = null;
              } else {
                payload.prestadores = [];
                payload.idPrestador = null;
                payload.idArt = null;
              }

              if (!isGestorSiniestrosRol(values.idRol)) {
                payload.referenteGestor = null;
              }

              handleSubmit(payload, addingNewUser);
            }}>
            {({ values, setFieldValue }) => (
              <Form>
                <LoadReferentesOnArt
                  open={open}
                  idRol={values.idRol}
                  idArt={values.idArt}
                  isGestorSiniestros={isGestorSiniestrosRol(values.idRol)}
                />

                <DialogContent>
                  <FormikTextField
                    margin="dense"
                    name="nombre"
                    label="Nombre"
                    type="text"
                    variant="standard"
                    fullWidth
                  />
                  <FormikTextField
                    margin="dense"
                    name="apellido"
                    label="Apellido"
                    variant="standard"
                    type="text"
                    fullWidth
                  />
                  <FormikTextField
                    margin="dense"
                    name="email"
                    label="Email"
                    variant="standard"
                    type="text"
                    fullWidth
                    disabled={!addingNewUser}
                  />
                  <StyledDialog.FullWidth>
                    <FormikSelect
                      name="idRol"
                      label="Rol"
                      callBack={(idRol) => handleRolChange(idRol, setFieldValue)}
                    >
                      {rolesList.map((r) => (
                        <MenuItem key={r.id} value={r.id}>
                          {r.tipo}
                        </MenuItem>
                      ))}
                    </FormikSelect>
                  </StyledDialog.FullWidth>
                  <StyledDialog.FullWidth>
                    {(isArtRol(values.idRol) || isTrasladosRol(values.idRol)) && (
                      <FormikAutocomplete
                        name="idArt"
                        label="Art"
                        options={arts}
                        variant="standard"
                        callBack={(art) => handleArtChange(art, setFieldValue)}
                      />
                    )}
                  </StyledDialog.FullWidth>
                  {isGestorSiniestrosRol(values.idRol) && (
                    <StyledDialog.FullWidth>
                      <FormikAutocomplete
                        name="referenteGestor"
                        label="Referente / Gestor"
                        options={referentes.data || []}
                        variant="standard"
                        showAllOptions
                        disabled={!values.idArt}
                      />
                    </StyledDialog.FullWidth>
                  )}
                  {!userRole?.PRESTADOR && (
                    <StyledDialog.FullWidth>
                      {isPrestadorRol(values.idRol) && (
                        <FormikSelectMultiple
                          name="prestadores"
                          label="Prestadores"
                          variant="standard"
                        >
                          {providers.map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                              {p.nombre}
                            </MenuItem>
                          ))}
                        </FormikSelectMultiple>
                      )}
                    </StyledDialog.FullWidth>
                  )}
                </DialogContent>
                <DialogActions>
                  <FormikActions handleCancel={handleClose} />
                </DialogActions>
              </Form>
            )}
          </FormikForm>
        </>
      )}
    </CustomDialog>
  );
};

export default EditUserDialog;
